import { useTheme } from '@mui/material';
import { useFeature } from '@next-box/feature-flags';
import { useMemo, useRef, useState } from 'react';
import { CarouselControls } from '#components/CarouselControls/index.tsx';
import { FeatureFlags } from '#enums/featureFlags.ts';
import { mapScreenFormatTypeToScreenType } from '#helpers/enumMappers.ts';
import { disable as disableFullscreenLock } from '#helpers/fullscreenLock.ts';
import { getItemsKey } from '#helpers/getItemsKey.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { type ActiveVideo } from '#types.ts';
import { Loader } from '../Loader/index.tsx';
import { PlayerSelectorModal } from '../Modal/PlayerSelectorModal.tsx';
import { Tile } from '../Tile/index.tsx';
import { type TileProps } from '../Tile/types.ts';
import { TileCarouselSkeleton } from './Skeleton.tsx';
import { CarouselContainer, CarouselScrollContainer, TileContainer } from './styled.ts';
import { type TileCarouselProps } from './types.ts';

export const TileCarousel = ({
  hasNextPage,
  imageType,
  items = [],
  lastItemRef,
  loading,
  renderImage,
  scrollRef,
  setActivePreview,
  tileContainerSx,
}: TileCarouselProps) => {
  const feature = useFeature(FeatureFlags.ExoticPinkPtarmigan);
  const { uaParser } = useToolbelt();
  const theme = useTheme();
  const [activeVideo, setActiveVideo] = useState<ActiveVideo | undefined>();
  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  // Unable to avoid due to argument type
  // eslint-disable-next-line unicorn/no-null
  const carouselScrollRef = useRef<HTMLDivElement | null>(scrollRef?.current ?? null);
  const itemsKey = getItemsKey(items);

  const memoCarousel = useMemo(
    () => (
      <>
        {items.map(({ id, ...rest }, index) => {
          const partialTileProps: Omit<TileProps, 'id' | 'setActiveVideo'> = rest;

          if (lastItemRef && index === items.length - 1) {
            partialTileProps.lastItemRef = lastItemRef;
          }

          return (
            <TileContainer
              key={`${String(id)}-${String(index)}`}
              sx={
                tileContainerSx ?? {
                  [theme.breakpoints.down('sm')]: {
                    width: '120px',
                  },
                  marginRight: theme.spacing(2),
                  width: '180px',
                }
              }
            >
              <Tile
                id={id}
                renderImage={renderImage}
                setActivePreview={setActivePreview}
                setActiveVideo={setActiveVideo}
                {...partialTileProps}
              />
            </TileContainer>
          );
        })}
      </>
    ),
    [itemsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      {activeVideo && (activeVideo.src.length > 0 || feature) ? (
        <PlayerSelectorModal
          activeVideo={activeVideo}
          onClose={() => {
            setActiveVideo(undefined);
            void disableFullscreenLock(uaParser);
          }}
          type={mapScreenFormatTypeToScreenType(activeVideo.type)}
        />
      ) : undefined}
      {items.length > 0 ? (
        <>
          <CarouselContainer ref={carouselContainerRef}>
            <CarouselScrollContainer ref={carouselScrollRef}>
              {memoCarousel}
              {hasNextPage && loading && <Loader sx={{ marginLeft: theme.spacing(3) }} />}
            </CarouselScrollContainer>
          </CarouselContainer>
          <CarouselControls
            containerRef={carouselContainerRef}
            hasNextPage={hasNextPage && loading}
            scrollRef={carouselScrollRef}
          />
        </>
      ) : (
        <>
          <TileCarouselSkeleton imageType={imageType} tileContainerSx={tileContainerSx} />
          <CarouselControls />
        </>
      )}
    </>
  );
};
